/**
 * Componente de métricas de pedidos por período
 * Orders period metrics component
 */

import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodTab } from './OrdersByPeriodChart';
import { GlobalMetrics, DailyMetric } from '../../models/LogisticsApiResponse';

interface OrdersPeriodMetricsProps {
  activeTab: PeriodTab;
  globalMetrics?: GlobalMetrics;
  dailyMetrics?: DailyMetric[];
  isLoading?: boolean;
}

export const OrdersPeriodMetrics = ({
  activeTab,
  globalMetrics,
  dailyMetrics,
  isLoading = false,
}: OrdersPeriodMetricsProps) => {
  const { t } = useTranslation('logisticsModule');
  const currency = useCurrency();

  // Calcular métricas según el tab activo
  // Calculate metrics based on active tab
  const metrics = useMemo(() => {
    // Para "all", usar global_metrics directamente (evita duplicar pedidos)
    // For "all", use global_metrics directly (avoids duplicating orders)
    if (activeTab === 'all' && globalMetrics) {
      // Calcular el total de importe usando amount_sent (evita duplicar importes)
      // Calculate total amount using amount_sent (avoids duplicating amounts)
      let totalAmount = 0;
      if (dailyMetrics && dailyMetrics.length > 0) {
        dailyMetrics.forEach((metric) => {
          totalAmount += metric.amount_sent;
        });
      }
      return {
        totalOrders: globalMetrics.total,
        totalAmount,
      };
    }

    // Para otros tabs, sumar desde daily_metrics
    // For other tabs, sum from daily_metrics
    if (!dailyMetrics || dailyMetrics.length === 0) {
      return { totalOrders: 0, totalAmount: 0 };
    }

    let totalOrders = 0;
    let totalAmount = 0;

    dailyMetrics.forEach((metric) => {
      switch (activeTab) {
        case 'sent':
          totalOrders += metric.orders_sent;
          totalAmount += metric.amount_sent;
          break;
        case 'delivered':
          totalOrders += metric.orders_delivered;
          totalAmount += metric.amount_delivered;
          break;
        case 'in_transit':
          totalOrders += metric.orders_in_transit;
          totalAmount += metric.amount_in_transit;
          break;
        case 'returned':
          totalOrders += metric.orders_returned;
          totalAmount += metric.amount_returned;
          break;
      }
    });

    return { totalOrders, totalAmount };
  }, [dailyMetrics, activeTab, globalMetrics]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Total pedidos - Total orders */}
      <div className="bg-card-bg border border-card-border rounded-lg px-3 py-2">
        <h3 className="text-sm font-medium text-complementary-text mb-2">
          {t('orders_by_period.metrics.total_orders')}
        </h3>
        <p className="text-base font-bold text-title">{isLoading ? '-' : metrics.totalOrders}</p>
      </div>

      {/* Total importe - Total amount */}
      <div className="border border-card-border rounded-lg px-3 py-2">
        <h3 className="text-sm font-medium text-complementary-text mb-2">
          {t('orders_by_period.metrics.total_amount')}
        </h3>
        <p className="text-base font-bold text-title">
          {isLoading ? '-' : formatCurrencyAmount(metrics.totalAmount, currency)}
        </p>
      </div>
    </div>
  );
};
